import { Box, Typography, Card, Stack, ListItem, ListItemAvatar, Avatar, ListItemText, Table, TableBody, TableRow, TableCell, IconButton, Tooltip } from "@mui/material";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import passportRequestConverter from "../../converters/passportRequestConverter";
import { Employee } from "../../types/Employee";
import { PassportRequest, PassportRequestStatus } from "../../types/PassportRequest";
import { queryEmployee } from "../../utils/queryEmployee";
import { Notification } from "../../types/Notification";
import { EnhancedTableCell } from "../EnhancedTableCell";
import { RunCircleRounded, MopedRounded, CheckCircleRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { MedicalPaperRequest, MedicalPaperRequestStatus } from "../../types/MedicalPaperRequest";
import medicalPaperRequestConverter from "../../converters/medicalPaperRequestConverter";
import { NotificationEmployeeHeading } from "./NotificationEmployeeHeading";
import { updateMedicalPaperRequestStatus } from "../../utils/updateMedicalPaperRequest";
import { MedicalPaperRequestInfoBox } from "../dashboard/medical/MedicalPaperRequestInfoBox";
import { RequestActionBox } from "../RequestActionBox";
import { UniformRequest, UniformRequestStatus } from "../../types/UniformRequest";
import { updateUniformRequestStatus } from "../../utils/updateUniformRequest";
import uniformRequestConverter from "../../converters/uniformRequestConverter";
import { UniformRequestInfoBox } from "../dashboard/uniform/UniformRequestInfoBox";

export const NotificationUniformRequest = (props: { notification: Notification }) => {
  const { notification } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uniformRequest, setUniformRequest] = useState<UniformRequest>();
  const [employee, setEmployee] = useState<Employee>();

  const db = getFirestore();

  const handleUpdateMedicalPaperRequest = (s: UniformRequestStatus) => {
    if (uniformRequest) {
      setUpdating(true);
      updateUniformRequestStatus(uniformRequest, s).then(() => {
        let message = `Employee '${uniformRequest.idNumber}' uniform request has been update.`;
        if (s === 'Processing')
          message = `Employee '${uniformRequest.idNumber}' uniform request is now being processed.`;
        else if (s === 'Available')
          message = `Employee '${uniformRequest.idNumber}' uniform request is now ready for pickup.`;
        else if (s === 'Completed')
          message = `Employee '${uniformRequest.idNumber}' uniform request is now tagged as complete.`;
        else if (s === 'Cancelled')
          message = `Employee '${uniformRequest.idNumber}' uniform request is now cancelled.`;

        setUniformRequest({ ...uniformRequest, status: s });
        enqueueSnackbar(message, { variant: 'success' });

        setUpdating(false);
      })
    }
  };

  useEffect(() => {
    if (uniformRequest?.idNumber !== employee?.idNumber) {
      setLoading(true);
      if (uniformRequest !== undefined) {
        queryEmployee(uniformRequest.idNumber).then(e => {
          setEmployee(e);
          setLoading(false);
        });
      }
    }
  }, [uniformRequest]);
  
  useEffect(() => {
    // Get passport request
    if (notification.pathToDocument) {
      getDoc(doc(db, notification.pathToDocument).withConverter(uniformRequestConverter)).then(qDocSnapshot => {
        if (qDocSnapshot.exists()) {
          setUniformRequest(qDocSnapshot.data());
        }
      });
    }
  }, []);

  return (
    <Box>
      <Typography variant='h4' mb={1}>Uniform Request</Typography>
      <Card>
        {loading ? (
          <Box p={1}>
            <Typography>Please wait...</Typography>
          </Box>
        ) : (
          <Box p={1}>
            <Stack direction='row' mb={2}>
              {employee && <NotificationEmployeeHeading employee={employee} />}
              {uniformRequest && (<RequestActionBox status={uniformRequest.status} onUpdateStatus={handleUpdateMedicalPaperRequest} isUpdating={updating} />)}
            </Stack>
            {uniformRequest && (
              <UniformRequestInfoBox uniformRequest={uniformRequest} />
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};